import { formatMoney, useResource } from '@landsbankinn/l16n';
import { formatAccountId } from '@landsbankinn/ui-format';
import {
  ArrowIcon24,
  Box,
  Checkbox,
  Col,
  Flex,
  InputContainer,
  Label,
  ListItem,
  Span,
  UnorderedList,
} from '@landsbankinn/ui-kit';
import { type Account, ChoiceType } from '../../../state/server';
import { res } from './Accounts.res';

function SubSection(props: {
  account: Account;
  variant?: 'single' | 'none';
  showName?: boolean;
}) {
  const { r, locale } = useResource();
  const label = formatAccountId(props.account.id) ?? props.account.id;
  const showName = props.showName ?? true;

  return (
    <Box paddingVertical="24">
      <Flex mainAxisAlignment="space between" crossAxisAlignment="center">
        {showName && (
          <Span
            fontFamily="LB Centro Serif Cond"
            fontWeight="medium"
            fontSize={
              props.variant === 'single' || props.variant === 'none'
                ? '22'
                : '18'
            }
            letterSpacing=".25"
            lineHeight="24"
            color="blue font"
          >
            {props.account.name}
          </Span>
        )}
        {props.variant === 'single' && <ArrowIcon24 />}
      </Flex>
      <Box paddingTop="10">
        <Span fontVariant="table">{label}</Span>
      </Box>
      <Box>
        <Span fontVariant="table">{`${r(res.availableAmount)}: ${formatMoney(
          locale,
          props.account.availableFunds,
          props.account.currency,
          true,
        )}`}</Span>
      </Box>
    </Box>
  );
}

export function AccountSelection(props: {
  availableAccounts: Account[];
  selectedAccountIds: string[];
  choiceType: ChoiceType;
  onChange?: (value: string) => void;
}) {
  const { r, locale } = useResource();
  const onChange = (accountId: string) => {
    if (props.onChange) {
      props.onChange(accountId);
    }
  };

  return (
    <UnorderedList animated={false}>
      {props.availableAccounts.map((account) => {
        return (
          <ListItem
            withHover={false}
            key={account.id}
            direction="row"
            borderBottom="thin"
          >
            {props.choiceType === ChoiceType.Single && (
              <Box
                width="100%"
                onClick={() => {
                  onChange(account.id);
                }}
              >
                <SubSection account={account} variant="single" />
              </Box>
            )}
            {props.choiceType === ChoiceType.Multiple && (
              <Flex crossAxisAlignment="start" paddingVertical="16" colGap="20">
                <Box paddingTop="4" onClick={() => onChange(account.id)}>
                  <InputContainer>
                    <Checkbox
                      id={account.id}
                      checked={props.selectedAccountIds.includes(account.id)}
                      onValueChange={() => {
                        onChange(account.id);
                      }}
                    />
                  </InputContainer>
                </Box>
                <Col>
                  <Label htmlFor={account.id}>
                    <Span
                      fontFamily="LB Centro Serif Cond"
                      fontWeight="medium"
                      fontSize="18"
                      letterSpacing=".25"
                      lineHeight="24"
                      color="blue font"
                    >
                      {account.name}
                    </Span>
                  </Label>
                  <Box paddingTop="8">
                    <Span fontVariant="table">
                      {formatAccountId(account.id) ?? account.id}
                    </Span>
                  </Box>
                  <Span fontVariant="table">{`${r(res.availableAmount)}: ${formatMoney(
                    locale,
                    account.availableFunds,
                    account.currency,
                    true,
                  )}`}</Span>
                </Col>
              </Flex>
            )}
            {props.choiceType === ChoiceType.None && (
              <SubSection account={account} variant="none" />
            )}
          </ListItem>
        );
      })}
    </UnorderedList>
  );
}
